import { debounce, partition } from 'lodash';
import React from 'react';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useCurrentUser } from '../current-user/current-user';
import { createStreamListener, useStream } from '../socket/use-stream';
import { EventNotificationsStore } from './event-notifications-store';
import { runInAction } from 'mobx';
import { CadNextNotificationTypeEnumType } from '@mark43/cad-api';

export const EventNotificationsContext = React.createContext<EventNotificationsStore | undefined>(
    undefined
);

const MASTER_PROFILE_MATCH_NOTIFICATION_TYPES: CadNextNotificationTypeEnumType[] = [
    'INVOLVED_PERSON_MASTER_SEARCH_RESULTS',
    'REPORTING_PARTY_MASTER_SEARCH_RESULTS',
    'TOW_VEHICLE_MASTER_SEARCH_RESULTS',
    'VEHICLE_MASTER_SEARCH_RESULTS',
];

export function EventNotificationsProvider(props: {
    children: React.ReactNode;
    eventNotificationsStore?: EventNotificationsStore;
}): React.ReactElement {
    const currentUser = useCurrentUser();
    const { postMessage: dismissEventNotificationsPostMessage } = useBroadcast(
        'dismiss-event-notifications'
    );
    const eventNotificationsStore: EventNotificationsStore = React.useState(
        () =>
            props.eventNotificationsStore ||
            new EventNotificationsStore({
                onRequestEventNotificationsState() {
                    requestEventNotificationsState();
                },
                onEventNotificationRemoval({ eventId, entityId, notifications }) {
                    // transmit the removal of this notification to other windows which
                    // have their own instance of the event notifications store
                    dismissEventNotificationsPostMessage({
                        type: 'DISMISS_CAD_NEXT_EVENT_NOTIFICATIONS',
                        payload: {
                            eventId,
                            entityId,
                            notifications,
                        },
                    });
                },
            })
    )[0];

    // when another window transmits that a notification is to be removed, remove it in
    // the local instance of the event notifications store
    // note: this is also called from legacy cad
    useBroadcast('dismiss-event-notifications', (message) => {
        if (message.type === 'DISMISS_CAD_NEXT_EVENT_NOTIFICATIONS') {
            eventNotificationsStore.removeNotifications({
                ...message.payload,
                options: {
                    localRemovalOnly: true,
                },
            });
        }
        if (message.type === 'DISMISS_CAD_NEXT_EVENT_NOTIFICATIONS_FOR_ENTITY_TYPE') {
            eventNotificationsStore.removeAllNotificationsForEntityTypeForEvent(
                message.payload.eventId,
                message.payload.entityType,
                {
                    localRemovalOnly: true,
                }
            );
        }
    });

    /**
     * Windows which open later than other windows are liable to be missing event notifications state held by older windows.
     * Given this, when a window mounts it will request event notifications state from other windows that already exist
     * to ensure that its own state is up to date.
     */
    const { postMessage: syncEventNotificationsPostMessage } = useBroadcast(
        'sync-event-notifications',
        (message) => {
            if (message.type === 'REQUEST_EVENT_NOTIFICATIONS_STATE') {
                const stateToTransmit = eventNotificationsStore.getState();
                syncEventNotificationsPostMessage({
                    type: 'SEND_EVENT_NOTIFICATIONS_STATE_FOR_MERGING',
                    payload: {
                        eventNotificationMap: stateToTransmit,
                    },
                });
            }
            if (message.type === 'SEND_EVENT_NOTIFICATIONS_STATE_FOR_MERGING') {
                eventNotificationsStore.mergeState(message.payload.eventNotificationMap);
            }
        }
    );

    const requestEventNotificationsState = React.useMemo(() => {
        // we debounce this to account for the case where multiple queues open
        // simultaneously during the loading of a workspace
        return debounce(() => {
            syncEventNotificationsPostMessage({
                type: 'REQUEST_EVENT_NOTIFICATIONS_STATE',
            });
        }, 100);
    }, [syncEventNotificationsPostMessage]);

    const streamListener = React.useMemo(() => {
        return createStreamListener({
            streamNames: ['cadNextNotifications'],
            onMessage(message) {
                runInAction(() => {
                    if (message.payload.actingUserId !== currentUser.userId) {
                        for (const key in message.payload.eventNotificationsMap) {
                            const eventNotifications = message.payload.eventNotificationsMap[key];
                            runInAction(() => {
                                const [toSet, toRemove] = partition(
                                    eventNotifications,
                                    (eventNotification) => {
                                        return eventNotification.notificationAction === 'CREATE';
                                    }
                                );
                                toSet.forEach((eventNotification) => {
                                    eventNotificationsStore.setNotification(eventNotification);
                                });
                                toRemove.forEach((eventNotification) => {
                                    eventNotificationsStore.removeNotification({
                                        eventId: eventNotification.eventId,
                                        entityId: eventNotification.entityId,
                                        notification: eventNotification.notificationType,
                                    });
                                });
                            });
                        }
                    } else {
                        const toSet = Object.values(message.payload.eventNotificationsMap)
                            .flat()
                            .filter((notification) =>
                                MASTER_PROFILE_MATCH_NOTIFICATION_TYPES.includes(
                                    notification.notificationType
                                )
                            );
                        toSet.forEach((eventNotification) => {
                            eventNotificationsStore.setNotification(eventNotification);
                        });
                    }
                });
            },
        });
    }, [currentUser.userId, eventNotificationsStore]);

    useStream(streamListener);

    return (
        <EventNotificationsContext.Provider value={eventNotificationsStore}>
            {props.children}
        </EventNotificationsContext.Provider>
    );
}
